import { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  Alert,
  ActivityIndicator,
  Image,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import DateTimePicker from "@react-native-community/datetimepicker";
import * as ImagePicker from "expo-image-picker";
import { readAsStringAsync } from "expo-file-system/legacy";
import { decode } from "base64-arraybuffer";
import { useAuth } from "@/providers/AuthProvider";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { logActivity } from "@/lib/activity-log";

/**
 * Form input component with dark mode support
 */
interface FormInputProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  keyboardType?: "default" | "email-address" | "phone-pad";
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  maxLength?: number;
}

const FormInput = ({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType = "default",
  autoCapitalize = "sentences",
  maxLength,
}: FormInputProps) => {
  const colors = useThemeColors();

  return (
    <View className="mb-4">
      <Text
        className="text-sm font-medium mb-2"
        style={{ color: colors.textSecondary }}
      >
        {label}
      </Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.textTertiary}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        maxLength={maxLength}
        className="rounded-xl px-4 py-3"
        style={{
          backgroundColor: colors.surfaceSecondary,
          color: colors.text,
          borderWidth: 1,
          borderColor: colors.border,
        }}
      />
    </View>
  );
};

/**
 * Edit Profile Screen
 * Allows users to update their profile information
 */
export default function EditProfileScreen() {
  const router = useRouter();
  const { profile, activeProfile, user, refreshProfile, isSwitched } = useAuth();
  const { isDark } = useTheme();
  const colors = useThemeColors();

  // Use activeProfile for editing (allows guardian to edit dependent's profile)
  const editingProfile = activeProfile || profile;

  // Form state
  const [fullName, setFullName] = useState(editingProfile?.full_name || "");
  const [nickname, setNickname] = useState(editingProfile?.nickname || "");
  const [phone, setPhone] = useState(editingProfile?.phone || "");
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(
    editingProfile?.date_of_birth ? new Date(editingProfile.date_of_birth) : null
  );
  const [avatarUrl, setAvatarUrl] = useState(editingProfile?.avatar_url || "");

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  /**
   * Handle date change from picker
   */
  const handleDateChange = (event: any, selectedDate?: Date) => {
    setShowDatePicker(Platform.OS === "ios");
    if (selectedDate) {
      setDateOfBirth(selectedDate);
    }
  };

  /**
   * Pick and upload avatar image
   */
  const handlePickAvatar = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert("Geen toegang", "We hebben toegang nodig tot je foto's.");
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
        presentationStyle: ImagePicker.UIImagePickerPresentationStyle.FULL_SCREEN,
      });

      if (!result.canceled && result.assets[0]) {
        setIsUploadingAvatar(true);

        // Upload to Supabase storage (use editingProfile ID for dependent uploads)
        const uri = result.assets[0].uri;
        const base64 = await readAsStringAsync(uri, { encoding: "base64" });
        const fileName = `${editingProfile?.id || user?.id}/avatar-${Date.now()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from("profile-pictures")
          .upload(fileName, decode(base64), {
            contentType: "image/jpeg",
            upsert: true,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);
          Alert.alert("Fout", "Kon afbeelding niet uploaden");
          setIsUploadingAvatar(false);
          return;
        }

        const { data: urlData } = supabase.storage
          .from("profile-pictures")
          .getPublicUrl(fileName);

        setAvatarUrl(`${urlData.publicUrl}?t=${Date.now()}`);
        setIsUploadingAvatar(false);
      }
    } catch (error) {
      console.error("Error picking avatar:", error);
      Alert.alert("Fout", "Kon afbeelding niet laden");
      setIsUploadingAvatar(false);
    }
  };

  /**
   * Save profile changes
   */
  const handleSave = async () => {
    if (!fullName.trim()) {
      Alert.alert("Fout", "Volledige naam is verplicht");
      return;
    }

    setIsSaving(true);

    // Update the correct profile (could be dependent or main user)
    const profileIdToUpdate = editingProfile?.id || user?.id;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: fullName.trim(),
          nickname: nickname.trim() || null,
          phone: phone.trim() || null,
          date_of_birth: dateOfBirth?.toISOString().split("T")[0] || null,
          avatar_url: avatarUrl || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", profileIdToUpdate);

      if (error) throw error;

      // Log activity
      await logActivity({
        user_id: user?.id, // Logged as the guardian's action
        action_type: "profile.update",
        action_category: "profile",
        target_type: "user",
        target_id: profileIdToUpdate,
        target_name: fullName.trim(),
      });

      // Refresh profile data
      await refreshProfile();

      Alert.alert("Opgeslagen", "Je profiel is bijgewerkt", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } catch (error: any) {
      console.error("Error saving profile:", error);
      Alert.alert("Fout", error.message || "Kon profiel niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  const initials = fullName
    ?.split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top"]}
    >
      {/* Header */}
      <View
        className="flex-row items-center px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <Pressable
          onPress={() => router.back()}
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.surfaceSecondary }}
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Ionicons name="arrow-back" size={20} color={colors.text} />
        </Pressable>
        <Text
          className="flex-1 text-center text-lg font-semibold mr-10"
          style={{ color: colors.text }}
        >
          Profiel Bewerken
        </Text>
      </View>

      <ScrollView
        className="flex-1"
        contentContainerClassName="px-4 py-6"
        showsVerticalScrollIndicator={false}
      >
        {/* Avatar Section */}
        <View className="items-center mb-8">
          <Pressable
            onPress={handlePickAvatar}
            disabled={isUploadingAvatar}
            className="relative"
            accessibilityRole="button"
            accessibilityLabel="Wijzig profielfoto"
          >
            <View
              className="w-28 h-28 rounded-full items-center justify-center overflow-hidden border-4"
              style={{
                backgroundColor: colors.primaryLight,
                borderColor: colors.primary,
              }}
            >
              {isUploadingAvatar ? (
                <ActivityIndicator size="large" color={colors.primary} />
              ) : avatarUrl ? (
                <Image
                  source={{ uri: avatarUrl }}
                  className="w-full h-full"
                  resizeMode="cover"
                />
              ) : (
                <Text
                  className="text-4xl font-bold"
                  style={{ color: colors.primary }}
                >
                  {initials}
                </Text>
              )}
            </View>
            <View
              className="absolute bottom-0 right-0 w-10 h-10 rounded-full items-center justify-center border-2"
              style={{
                backgroundColor: colors.primary,
                borderColor: colors.background,
              }}
            >
              <Ionicons name="camera" size={18} color="#FFFFFF" />
            </View>
          </Pressable>
          <Text
            className="text-sm mt-3"
            style={{ color: colors.textSecondary }}
          >
            Tik om foto te wijzigen
          </Text>
        </View>

        {/* Form Fields */}
        <FormInput
          label="Volledige Naam *"
          value={fullName}
          onChangeText={setFullName}
          placeholder="Je volledige naam"
          autoCapitalize="words"
          maxLength={100}
        />

        <FormInput
          label="Bijnaam"
          value={nickname}
          onChangeText={setNickname}
          placeholder="Optionele bijnaam"
          maxLength={50}
        />

        <FormInput
          label="Telefoonnummer"
          value={phone}
          onChangeText={setPhone}
          placeholder="+31 6 12345678"
          keyboardType="phone-pad"
          maxLength={20}
        />

        {/* Date of Birth */}
        <View className="mb-4">
          <Text
            className="text-sm font-medium mb-2"
            style={{ color: colors.textSecondary }}
          >
            Geboortedatum
          </Text>
          <Pressable
            onPress={() => setShowDatePicker(true)}
            className="rounded-xl px-4 py-3 flex-row items-center justify-between"
            style={{
              backgroundColor: colors.surfaceSecondary,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <Text style={{ color: dateOfBirth ? colors.text : colors.textTertiary }}>
              {dateOfBirth
                ? dateOfBirth.toLocaleDateString("nl-NL", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : "Selecteer datum"}
            </Text>
            <Ionicons name="calendar-outline" size={20} color={colors.textSecondary} />
          </Pressable>
        </View>

        {/* Date Picker Modal (Android shows as dialog, iOS inline) */}
        {showDatePicker && (
          <View
            className="rounded-xl overflow-hidden mb-4"
            style={{ backgroundColor: colors.surface }}
          >
            <DateTimePicker
              value={dateOfBirth || new Date(1990, 0, 1)}
              mode="date"
              display={Platform.OS === "ios" ? "spinner" : "default"}
              onChange={handleDateChange}
              maximumDate={new Date()}
              minimumDate={new Date(1920, 0, 1)}
            />
            {Platform.OS === "ios" && (
              <View className="flex-row justify-end px-4 pb-4">
                <Pressable
                  onPress={() => setShowDatePicker(false)}
                  className="px-4 py-2 rounded-lg"
                  style={{ backgroundColor: colors.primary }}
                >
                  <Text className="text-white font-medium">Klaar</Text>
                </Pressable>
              </View>
            )}
          </View>
        )}

        {/* Clear birthday button */}
        {dateOfBirth && (
          <Pressable
            onPress={() => setDateOfBirth(null)}
            className="mb-6"
          >
            <Text style={{ color: colors.error }} className="text-sm">
              Geboortedatum verwijderen
            </Text>
          </Pressable>
        )}

        {/* Save Button */}
        <Pressable
          onPress={handleSave}
          disabled={isSaving}
          className="rounded-xl py-4 items-center mt-4"
          style={{
            backgroundColor: isSaving ? colors.textTertiary : colors.primary,
          }}
          accessibilityRole="button"
          accessibilityLabel="Opslaan"
        >
          {isSaving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text className="text-white font-bold text-lg">Opslaan</Text>
          )}
        </Pressable>

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}

